import { AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { TOOLTIP_DELAY } from '@ardoq/tooltip';
import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColDef,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CustomEditorModule,
  EventApiModule,
  ExternalFilterModule,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridStateModule,
  ICellRendererParams,
  IsFullWidthRowParams,
  LocaleModule,
  RowHeightParams,
  PaginationModule,
  RenderApiModule,
  RowApiModule,
  RowClickedEvent,
  RowDragModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  TextEditorModule,
  TooltipModule,
  ValidationModule,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ClipboardModule,
  ColumnMenuModule,
  ContextMenuModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  TreeDataModule,
} from 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AnyRow,
  isFullWidthRow,
  isGhostRow,
  Row,
  UseControlledAGGridProps,
} from './types';
import {
  createGetRowClass,
  getGroupingColumn,
  getReorderableProps,
} from './utils';
import { getColumnDefs, PartialInitialColumnState } from './columnDefs';
import { useSelectable } from './hooks/useSelectable';
import { useEditable } from './hooks/useEditable';
import { useSortable } from './hooks/useSortable';
import { usePinnable } from './hooks/usePinnable';
import { usePagination } from './hooks/usePagination';
import { useScrollable } from './hooks/useScrollable';
import { useHighlightable } from './hooks/useHighlightable';
import { useGrouping } from './hooks/useGrouping';
import { useClipboard } from './hooks/useClipboard';
import { useColumnGrouping } from './hooks/useColumnGrouping';
import { useDragAndDrop, useRowAriaExpanded } from './hooks/useDragAndDrop';
import { useVisibleColumns } from './hooks/useVisibleColumns';
import { useColumnOrder } from './hooks/useColumnOrder';
import { useContextMenuItems } from './hooks/useContextMenuItems';
import { useClearCellSelectionOnOutsideClick } from './hooks/useClearCellSelectionOnOutsideClick';
import { useTopLayerPopupParent } from './hooks/useTopLayerPopupParent';
import { DEFAULT_TABLE_LOCALE_TEXT_LABELS } from '../labels';
import {
  SelectionCellRenderer,
  SelectionCellRendererParams,
  SelectionColumnHeader,
  SelectionColumnHeaderParams,
} from './SelectionColumn';

export {
  get2DArrayFromClipboardData,
  getPasteRows,
  getSelectionBounds,
  repeatDataToFillSelection,
} from './clipboard';
export type { SelectionBounds } from './clipboard';
export type { UseControlledAGGridProps } from './types';

// AG Grid provides modules for code-splitting, but since we need to import them anyways, they can be
// included directly in the gridOptions.
const MODULES = [
  CellSelectionModule,
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ContextMenuModule,
  CustomEditorModule,
  EventApiModule,
  ExternalFilterModule,
  GridStateModule,
  LocaleModule,
  PaginationModule,
  RenderApiModule,
  RowApiModule,
  RowDragModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  TextEditorModule,
  TooltipModule,
  TreeDataModule,
  ValidationModule,
];

const defaultLocaleText = {
  ...AG_GRID_LOCALE_EN,
  // Default: Drag here to set row groups
  // Changed to clarify what the user should drag
  rowGroupColumnsEmptyMessage:
    DEFAULT_TABLE_LOCALE_TEXT_LABELS.rowGroupColumnsEmptyMessage,
  // AG Grid's own selection strings describe the mechanics ("Press Space to
  // toggle row selection", "Column with Header Selection") rather than the
  // action. These defaults must live here, not only in
  // DEFAULT_TABLE_LOCALE_TEXT_LABELS, or they would apply only to consumers
  // that pass tableLocaleTextLabels.
  ariaRowToggleSelection:
    DEFAULT_TABLE_LOCALE_TEXT_LABELS.ariaRowToggleSelection,
  ariaRowSelectionDisabled:
    DEFAULT_TABLE_LOCALE_TEXT_LABELS.ariaRowSelectionDisabled,
  ariaHeaderSelection: DEFAULT_TABLE_LOCALE_TEXT_LABELS.ariaHeaderSelection,
  ariaRowSelectAll: DEFAULT_TABLE_LOCALE_TEXT_LABELS.ariaRowSelectAll,
};

/**
 * Wraps a full-width cell renderer so that the row automatically resizes
 * to match the rendered content height. AG Grid does not natively support
 * auto-height for full-width rows, so we use a ResizeObserver to measure
 * the content and update the row height via the AG Grid API.
 */
const createAutoSizingFullWidthRenderer =
  <RowData>(renderer: (data: RowData) => React.ReactNode) =>
  (params: ICellRendererParams<Row<RowData>>) => {
    const rowData = params.data?.data;
    if (!rowData) {
      return null;
    }
    return React.createElement(AutoSizingFullWidthCell, {
      params: params as ICellRendererParams<Row<unknown>>,
      renderer: () => renderer(rowData),
    });
  };

const AutoSizingFullWidthCell = ({
  params,
  renderer,
}: {
  params: ICellRendererParams<Row<unknown>>;
  renderer: () => React.ReactNode;
}) => {
  useEffect(() => {
    const container = params.eParentOfValue;

    Object.assign(container.style, {
      textWrap: 'wrap',
      transition: 'height 50ms ease-in-out',
    });
    container.setAttribute('role', 'gridcell');

    const updateRowHeight = () => {
      // Re-query the content child on every measurement: full-width content can
      // mount asynchronously (e.g. a stream-connected component that renders
      // null until its first emission), so the child present when this effect
      // first ran is not necessarily the one that determines the final height.
      // We measure the content element directly (not container.scrollHeight)
      // because once AG Grid sets a larger row height, the container expands to
      // fill it and scrollHeight never reports a smaller value.
      const content = container.firstElementChild;
      const measuredHeight =
        content && content.isConnected
          ? Math.ceil(content.getBoundingClientRect().height)
          : Math.ceil(container.scrollHeight);
      /** Add 1px to prevent AG Grid's bottom border from being cropped */
      const contentHeight = measuredHeight + 1;
      const currentRowHeight = params.node.rowHeight;

      if (measuredHeight > 0 && contentHeight !== currentRowHeight) {
        params.node.setRowHeight(contentHeight);
        params.api.onRowHeightChanged();
        // Animate the container to the new height in sync with AG Grid
        container.style.height = `${contentHeight}px`;
      }
    };

    const observer = new ResizeObserver(() => {
      updateRowHeight();
    });

    // Keep the ResizeObserver pointed at the live content child. When the child
    // is replaced (async mount/remount), re-observe the new node and re-measure.
    let observedContent: Element | null = null;
    const trackContent = () => {
      const content = container.firstElementChild;
      if (content !== observedContent) {
        if (observedContent) observer.unobserve(observedContent);
        if (content) observer.observe(content);
        observedContent = content;
      }
      updateRowHeight();
    };

    const mutationObserver = new MutationObserver(trackContent);
    mutationObserver.observe(container, { childList: true });

    trackContent();

    // Trigger initial measurement after content renders
    const rafId = requestAnimationFrame(trackContent);

    const updateColSpan = () => {
      const count = params.api.getAllDisplayedColumns().length;
      container.setAttribute('aria-colspan', String(count));
    };

    updateColSpan();
    params.api.addEventListener('displayedColumnsChanged', updateColSpan);

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      cancelAnimationFrame(rafId);
      params.api.removeEventListener('displayedColumnsChanged', updateColSpan);
    };
  }, [params.api, params.node, params.eParentOfValue]);

  return renderer();
};

const setAriaAttributes = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  api: GridApi,
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>
) => {
  for (const [key, value] of Object.entries(props)) {
    if (key.startsWith('aria-') && value !== undefined) {
      api.setGridAriaProperty(key.replace('aria-', ''), value);
    }
  }
};

const onGridReady = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  event: GridReadyEvent,
  apiRef: React.RefObject<GridApi | null>,
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  handleInitialScroll: (api: GridApi) => void,
  handleInitialDropZones: (api: GridApi) => void
) => {
  apiRef.current = event.api;
  setAriaAttributes(event.api, props);
  handleInitialScroll(event.api);
  handleInitialDropZones(event.api);
};

type ControlledAGGridProps<
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
> = AgGridReactProps<AnyRow<RowData, FullWidthRowData, RowId>> & {
  containerRef: React.RefObject<HTMLDivElement | null>;
};

export const useControlledAGGrid = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>
): ControlledAGGridProps<RowData, FullWidthRowData, RowId> => {
  const apiRef = useRef<GridApi | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const api = apiRef.current;
  const rowsRef = useRef(props.rows);
  const reorderableRef = useRef(props.reorderable);
  const fullWidthRowRendererRef = useRef(props.fullWidthRowRenderer);
  const getRowHeightRef = useRef(props.getRowHeight);
  const renderEmptyStateRef = useRef(props.renderEmptyState);

  useEffect(() => {
    rowsRef.current = props.rows;
  }, [props.rows]);

  useEffect(() => {
    getRowHeightRef.current = props.getRowHeight;
    if (api && props.getRowHeight) {
      api.resetRowHeights();
    }
  }, [props.getRowHeight, api]);

  // Keep the ref updated with the latest reorderable prop
  useEffect(() => {
    reorderableRef.current = props.reorderable;
  }, [props.reorderable]);

  // Keep the ref updated with the latest fullWidthRowRenderer prop
  useEffect(() => {
    fullWidthRowRendererRef.current = props.fullWidthRowRenderer;
  }, [props.fullWidthRowRenderer]);

  const groupingColumn = getGroupingColumn<RowData, RowId>(props.columns);

  const {
    selectState,
    selectionGridProps,
    getRowLabel,
    selectionColumnHeaderLabel,
    ghostRows,
    externalFilterGridProps,
  } = useSelectable(props, api);
  const { editGridProps } = useEditable(props, api, groupingColumn);
  const { sortableDescriptor, sortingGridProps } = useSortable(
    props,
    api,
    groupingColumn
  );
  const { pinnedColumns, pinningGridProps } = usePinnable(props, api);
  const { paginationGridProps } = usePagination(props, api);
  const { clipboardProps } = useClipboard(props, api);
  const { columnGroupingProps, columnGroups } = useColumnGrouping(props, api);
  const {
    externalDropZoneEnabled,
    handleInitialDropZones,
    dragAndDroppableProps,
  } = useDragAndDrop(props, groupingColumn);
  const { visibleColumnIds } = useVisibleColumns(props, api);
  const { columnOrder } = useColumnOrder(props, api);
  const { scrollableGridProps, handleInitialScroll } = useScrollable(
    props,
    api
  );
  const { highlightedRowIdsRef } = useHighlightable(props, api);

  const { popupHost, popupHostGridProps } = useTopLayerPopupParent();

  useClearCellSelectionOnOutsideClick(containerRef, apiRef, popupHost);

  useRowAriaExpanded(api, groupingColumn?.expandedIds);

  const { reorderableEnabled, reorderableProps } = getReorderableProps<
    RowData,
    RowId
  >(props.reorderable, reorderableRef, rowsRef);
  const suppressAllHeaderMenus =
    !props.enableColumnChooser &&
    props.pinnable === undefined &&
    props.sortable === undefined;

  const partialInitialColumnState: PartialInitialColumnState<RowData> = {
    reorderableEnabled: reorderableEnabled || externalDropZoneEnabled,
    groupedColumns: columnGroups,
    pinnedColumns,
    sortableDescriptor,
    enableColumnChooser: props.enableColumnChooser ?? false,
    suppressAllHeaderMenus,
    visibleColumnIds,
  };

  const { groupingEnabled, groupingProps } = useGrouping(
    props,
    api,
    partialInitialColumnState,
    groupingColumn
  );

  const pendingGroupColumnRefreshRef = useRef(false);

  const refreshGroupColumnCells = useCallback(() => {
    if (!api || !groupingEnabled) {
      return;
    }
    api.refreshCells({ columns: ['ag-Grid-AutoColumn'], force: true });
  }, [api, groupingEnabled]);

  // Refresh group column cells when rows change (e.g., after drag and drop)
  // so the cell renderer picks up the new node.level for correct indentation.
  // While a cell is being edited, forcing a refresh unmounts its editor and
  // drops in-progress input, so defer the refresh until editing stops.
  useEffect(() => {
    if (!api || !groupingEnabled) {
      return;
    }
    if (api.getEditingCells().length > 0) {
      pendingGroupColumnRefreshRef.current = true;
      return;
    }
    refreshGroupColumnCells();
  }, [api, groupingEnabled, props.rows, refreshGroupColumnCells]);

  const editedCoordinate = props.editable?.coordinate ?? null;
  useEffect(() => {
    if (editedCoordinate !== null || !pendingGroupColumnRefreshRef.current) {
      return;
    }
    pendingGroupColumnRefreshRef.current = false;
    refreshGroupColumnCells();
  }, [editedCoordinate, refreshGroupColumnCells]);

  const hasGetRowHeight = Boolean(props.getRowHeight);

  const hasFullWidthRenderer = props.fullWidthRowRenderer !== undefined;

  const fullWidthCellRenderer = useMemo(() => {
    if (!hasFullWidthRenderer) {
      return undefined;
    }

    // Use the auto-sizing wrapper to enable dynamic height.
    // The cast is safe: AG Grid passes the row's `.data` field which is
    // `FullWidthRowData` at runtime for full-width rows, but typed as `RowData`
    // because AG Grid only knows about `Row<RowData>`.
    return createAutoSizingFullWidthRenderer<RowData>((data: RowData) => {
      const renderer = fullWidthRowRendererRef.current;
      return renderer ? renderer(data as unknown as FullWidthRowData) : null;
    });
  }, [hasFullWidthRenderer]);

  const { renderEmptyState } = props;
  const hasEmptyStateRenderer = renderEmptyState !== undefined;

  // `noRowsOverlayComponent` is passed inside `gridOptions`, which AG Grid
  // reads once at grid creation and never diffs again, so the component must
  // keep a stable identity and read the current renderer through a ref.
  const noRowsOverlayComponent = useMemo(
    () =>
      hasEmptyStateRenderer
        ? () => renderEmptyStateRef.current?.() ?? null
        : undefined,
    [hasEmptyStateRenderer]
  );

  // A ref update alone cannot re-render an overlay that is already mounted, so
  // re-show it whenever the renderer changes while the grid has no rows.
  useEffect(() => {
    renderEmptyStateRef.current = renderEmptyState;
    if (!api || !renderEmptyState || props.loading) {
      return;
    }
    if (api.getDisplayedRowCount() === 0) {
      api.showNoRowsOverlay();
    }
  }, [api, renderEmptyState, props.loading, props.rows]);

  // Merge precedence (later wins):
  // 1. defaultLocaleText            — English fallback baked into the package
  // 2. props.tableLocaleTextLabels  — i18n override supplied by Pattern A consumers
  // 3. props.localeText             — raw AG Grid override; consumer-provided keys win
  const localeText = useMemo(
    () => ({
      ...defaultLocaleText,
      ...props.tableLocaleTextLabels,
      ...props.localeText,
    }),
    [props.tableLocaleTextLabels, props.localeText]
  );

  // Column definitions. Returned as a top-level prop (not inside gridOptions)
  // so that AG Grid React's reactive prop diffing applies updates when columns
  // are added or removed. AG Grid preserves user-modified column state (width,
  // sort, pinning, visibility) by matching colId across updates.
  //
  // Memoised on the set of column ids only — other column state (visibility,
  // order, pinning, sorting, grouping) is applied imperatively from dedicated
  // hooks via `applyColumnState`, so we don't want to rebuild colDefs and
  // disturb that state on every render.
  const columnIdsKey = props.columns.map(column => String(column.id)).join('|');
  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        columns: props.columns,
        columnOrder,
        initialColumnState: {
          ...partialInitialColumnState,
          groupingEnabled,
        },
        // Default flex fills width-less columns; opt out when the grid
        // auto-sizes columns, where flex would override the strategy.
        ...(props.autoSizeStrategy && { suppressFlexDefault: true }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: rebuild only when column ids change
    [columnIdsKey]
  );

  // `selectionColumnDef` is a managed AG Grid property: a new object identity
  // rebuilds the selection column. `getRowLabel` is a stable wrapper that reads
  // the live selectable prop at call time, so this memo effectively never
  // changes and the cached cellRendererParams can never go stale.
  const selectionColumnDef: ColDef<Row<RowData, RowId>> = useMemo(
    () => ({
      // Suppress context menu on checkbox column when in multi-select mode
      suppressHeaderContextMenu: true,
      // Give each row checkbox a per-row accessible name, and name the
      // otherwise anonymous selection column header.
      cellRenderer: SelectionCellRenderer,
      cellRendererParams: {
        getRowLabel,
      } satisfies SelectionCellRendererParams,
      headerComponent: SelectionColumnHeader,
      headerComponentParams: {
        label: selectionColumnHeaderLabel,
      } satisfies SelectionColumnHeaderParams,
    }),
    [getRowLabel, selectionColumnHeaderLabel]
  );

  const gridOptions: GridOptions<Row<RowData, RowId>> = {
    localeText,
    // Is used to sync with the externally managed props for selection and sorting.
    initialState: {
      ...selectState,
    },
    suppressMovableColumns: props.enableMovableColumns !== true,
    suppressDragLeaveHidesColumns: true,
    getRowId: params => params.data.id,
    onGridReady: event =>
      onGridReady(
        event,
        apiRef,
        props,
        handleInitialScroll,
        handleInitialDropZones
      ),
    tooltipShowDelay: TOOLTIP_DELAY,
    ...(props.headerHidden && { headerHeight: 0 }),
    ...(props.autoSizeStrategy && { autoSizeStrategy: props.autoSizeStrategy }),

    ...reorderableProps,
    // TODO: Remove cast when gridOptions is typed as AnyRow
    ...(sortingGridProps as GridOptions<Row<RowData, RowId>> | undefined),
    ...selectionGridProps,
    ...editGridProps,
    // TODO: Remove cast when gridOptions is typed as AnyRow
    ...(paginationGridProps as GridOptions<Row<RowData, RowId>> | undefined),
    ...groupingProps,
    ...columnGroupingProps,
    ...clipboardProps,
    ...pinningGridProps,
    ...dragAndDroppableProps,
    ...scrollableGridProps,
    ...externalFilterGridProps,

    selectionColumnDef,

    getContextMenuItems: useContextMenuItems(props),
    ...popupHostGridProps,
    // Styling
    getRowClass: createGetRowClass(highlightedRowIdsRef),
    onRowClicked: (event: RowClickedEvent<Row<RowData, RowId>>) => {
      const row = event.data;
      if (!row?.onClick) {
        return;
      }
      row.onClick();
    },

    ...(noRowsOverlayComponent && { noRowsOverlayComponent }),

    ...(props.autoHeight && { domLayout: 'autoHeight' as const }),

    // Row height computed from row data
    ...(hasGetRowHeight && {
      getRowHeight: (params: RowHeightParams<Row<RowData, RowId>>) => {
        const row = params.data;
        if (!row || isFullWidthRow(row) || isGhostRow(row)) return undefined;
        return getRowHeightRef.current?.(row.data);
      },
    }),

    // Full-width row support with auto-sizing via ResizeObserver
    ...(hasFullWidthRenderer && {
      isFullWidthRow: (params: IsFullWidthRowParams<Row<RowData, RowId>>) => {
        const row = params.rowNode.data;
        return row !== null && row !== undefined && isFullWidthRow(row);
      },
      fullWidthCellRenderer,
    }),
  };

  return {
    // AG Grid's TData parameter flows into every callback type (e.g.
    // RowClickedEvent<TData>), so GridOptions<Row> is not structurally
    // assignable to GridOptions<AnyRow>. At runtime only Row instances reach
    // column callbacks — full-width rows are routed through
    // isFullWidthRow/fullWidthCellRenderer and ghost rows are hidden by the
    // external filter. A single cast at this boundary is the minimal
    // concession to AG Grid's single-parameter type model.
    gridOptions: gridOptions as GridOptions<
      AnyRow<RowData, FullWidthRowData, RowId>
    >,
    modules: MODULES,
    // Top-level columnDefs is reactive — AG Grid React diffs and applies
    // changes via the API when columns are added or removed.
    columnDefs: columnDefs as ColDef<
      AnyRow<RowData, FullWidthRowData, RowId>
    >[],
    // Merge ghost rows (selected but filtered-out) into the row data so AG Grid
    // maintains row nodes for them. They are hidden via external filter.
    rowData: [...props.rows, ...ghostRows],
    // Ref to the wrapper div that holds the grid. Used by the outside-click
    // handler to determine whether a click landed inside the table.
    containerRef,
  };
};
